<template>
  <Popover>
    <PopoverTrigger as-child>
      <Button variant="ghost" size="sm" class="flex items-center gap-2">
        <Icon name="lucide:languages" class="h-5 w-5" />
        <Icon name="lucide:chevron-down" class="h-4 w-4" />
      </Button>
    </PopoverTrigger>
    <PopoverContent class="w-[200px] p-2">
      <Command>
        <CommandInput :placeholder="$t('common.searchLanguage')" class="h-9" />
        <CommandList>
          <CommandEmpty>{{ $t("common.noLanguageFound") }}</CommandEmpty>
          <CommandGroup>
            <CommandItem v-for="locale in availableLocales" :key="locale.code" :value="locale.code"
              @select="changeLanguage(locale.code)" class="flex items-center gap-2 cursor-pointer">
              <span class="flex-1">{{ getLanguageName(locale.code) }}</span>
              <Icon v-if="currentLocale === locale.code" name="lucide:check" class="h-4 w-4" />
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </Command>
    </PopoverContent>
  </Popover>
</template>

<script setup lang="ts">
import { SUPPORTED_LANGUAGES } from "@/utils/languages";
const { locale, locales, setLocale } = useI18n();
const currentLocale = computed(() => locale.value);
const { user } = useUser();

// List of available locales
const availableLocales = computed(() =>
  locales.value.map((l) => ({
    code: l.code,
    name: getLanguageName(l.code),
  }))
);

// Current language name
const currentLanguageName = computed(() =>
  getLanguageName(currentLocale.value)
);

// Function to get language name by code
function getLanguageName(code: string) {
  const language = SUPPORTED_LANGUAGES.find((l) => l.code === code);
  return language ? language.name : code.toUpperCase();
}

// Function to change language
const changeLanguage = async (newLocale: string) => {
  try {
    // Set the locale in i18n
    setLocale(newLocale);

    // Only try to save to database if user is logged in
    if (user.value && user.value.id) {
      try {
        const response = await $fetch("/api/user/interface-language", {
          method: "POST",
          body: {
            interface_language: newLocale,
          },
        });

        if (response.success) {
          // Update user object with new interface language
          user.value.interface_language = newLocale;
        }
      } catch (error) {
        console.error("Error saving language preference:", error);
        // Don't show error toast as this is secondary functionality
      }
    }
  } catch (error) {
    console.error("Error changing language:", error);
  }
};
</script>
